<template>
  <div class="products-page">
    <!-- Background Elements -->
    <div class="page-grid"></div>
    <div class="page-orb orb-1"></div>
    <div class="page-orb orb-2"></div>

    <!-- Main Container -->
    <div class="products-container">
      <!-- Header Card -->
      <div class="products-header">
        <div class="header-left">
          <div class="header-icon">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M28 10.6667L16 4L4 10.6667V21.3333C4 22.0406 4.28095 22.719 4.78105 23.219C5.28115 23.719 5.95942 24 6.66667 24H25.3333C26.0406 24 26.719 23.719 27.219 23.219C27.719 22.719 28 22.0406 28 21.3333V10.6667Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M16 28V16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="header-text">
            <h1>Products</h1>
            <p>Create, polish, and track your offerings</p>
          </div>
        </div>
        <div class="header-right">
          <div class="search-box">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <input v-model="search" type="text" placeholder="Search products..." />
          </div>
          <button class="btn-new-product" @click="openCreate">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 4.16667V15.8333M4.16667 10H15.8333" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            New Product
          </button>
        </div>
      </div>

      <!-- Products Table -->
      <div class="products-table-card">
        <div v-if="loading" class="table-loading">
          <div class="loader"></div>
          <p>Loading products...</p>
        </div>

        <div v-else-if="filtered.length === 0" class="table-empty">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <path d="M56 21.3333L32 8L8 21.3333V42.6667C8 44.0812 8.5619 45.4377 9.5621 46.4379C10.5623 47.4381 11.9188 48 13.3333 48H50.6667C52.0812 48 53.4377 47.4381 54.4379 46.4379C55.4381 45.4377 56 44.0812 56 42.6667V21.3333Z" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M32 56V32" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <p>No products yet</p>
          <span>Click <strong>New Product</strong> to add your first</span>
        </div>

        <div v-else class="table-content">
          <div class="table-header">
            <div class="th th-name">Name</div>
            <div class="th th-status">Status</div>
            <div class="th th-updated">Updated</div>
            <div class="th th-actions">Actions</div>
          </div>

          <div class="table-body">
            <div
              v-for="product in filtered"
              :key="product.id"
              class="table-row"
              @click="openWorkspace(product)"
            >
              <div class="td td-name">
                <div :class="['product-avatar', `avatar-${avatarColor(product.name)}`]">
                  {{ initials(product.name) }}
                </div>
                <div class="product-info">
                  <div class="product-name">{{ product.name }}</div>
                  <div class="product-summary">{{ product.summary }}</div>
                </div>
              </div>

              <div class="td td-status">
                <div :class="['status-badge', `status-${product.status}`]">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path v-if="product.status === 'active'" d="M13.3334 4L6.00002 11.3333L2.66669 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path v-else-if="product.status === 'paused'" d="M5.33335 3.33334H6.66669V12.6667H5.33335V3.33334ZM9.33335 3.33334H10.6667V12.6667H9.33335V3.33334Z" fill="currentColor"/>
                    <path v-else d="M2 3.33334H14V5.33334H2V3.33334ZM5.33333 7.33334H10.6667V13.3333H5.33333V7.33334Z" fill="currentColor"/>
                  </svg>
                  {{ product.status }}
                </div>
              </div>

              <div class="td td-updated">
                <div class="updated-info">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M8 4V8L10.6667 9.33333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span class="updated-date">{{ prettyTs(product.updatedAt) }}</span>
                </div>
                <div class="updated-ago">{{ since(product.updatedAt) }}</div>
              </div>

              <div class="td td-actions">
                <button class="action-btn btn-open" @click.stop="openWorkspace(product)" title="Open workspace">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M15 9.75L9.75 15M9.75 15L4.5 9.75M9.75 15V3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <button class="action-btn btn-edit" @click.stop="edit(product)" title="Edit">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M13.5 2.25C13.6989 2.05109 13.9645 1.93934 14.2425 1.93934C14.3809 1.93934 14.5182 1.96663 14.6463 2.01987C14.7744 2.07311 14.891 2.15121 14.9897 2.24989C15.0884 2.34858 15.1665 2.46586 15.2197 2.59397C15.273 2.72208 15.3002 2.85938 15.3002 2.99777C15.3002 3.13617 15.273 3.27347 15.2197 3.40158C15.1665 3.52969 15.0884 3.64697 14.9897 3.74565L5.25 13.5L1.5 14.625L2.625 10.875L12.3638 1.13625C12.5627 0.937342 12.8283 0.825592 13.1063 0.825592C13.2447 0.825592 13.382 0.852882 13.5101 0.906121C13.6382 0.95936 13.7548 1.03746 13.8535 1.13615C13.9522 1.23483 14.0303 1.35211 14.0835 1.48022C14.1368 1.60833 14.164 1.74563 14.164 1.88403C14.164 2.02242 14.1368 2.15972 14.0835 2.28783C14.0303 2.41594 13.9522 2.53322 13.8535 2.6319L13.5 2.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <button
                  class="action-btn btn-delete"
                  :disabled="deletingId === product.id"
                  @click.stop="removeItem(product)"
                  title="Delete"
                >
                  <svg v-if="deletingId !== product.id" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M2.25 4.5H3.75H15.75M6 4.5V3C6 2.60218 6.15804 2.22064 6.43934 1.93934C6.72064 1.65804 7.10218 1.5 7.5 1.5H10.5C10.8978 1.5 11.2794 1.65804 11.5607 1.93934C11.842 2.22064 12 2.60218 12 3V4.5M14.25 4.5V15C14.25 15.3978 14.092 15.7794 13.8107 16.0607C13.5294 16.342 13.1478 16.5 12.75 16.5H5.25C4.85218 16.5 4.47064 16.342 4.18934 16.0607C3.90804 15.7794 3.75 15.3978 3.75 15V4.5H14.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <div v-else class="mini-loader"></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Dialog -->
    <div v-if="dlg" class="dialog-overlay" @click="dlg = false">
      <div class="dialog-card" @click.stop>
        <div class="dialog-header">
          <div class="dialog-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 22V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>{{ form.id ? 'Edit Product' : 'Create Product' }}</span>
          </div>
          <div class="slug-preview">{{ form.slug || slugify(form.name) }}</div>
          <button class="dialog-close" @click="dlg = false">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <div class="dialog-body">
          <div class="form-group">
            <label>Name</label>
            <input v-model="form.name" type="text" placeholder="Product name" @input="autoSlug" />
          </div>

          <div class="form-group">
            <label>Slug (unique)</label>
            <input v-model="form.slug" type="text" placeholder="product-slug" />
          </div>

          <div class="form-group">
            <label>Status</label>
            <select v-model="form.status">
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div class="form-group">
            <label>Summary</label>
            <textarea v-model="form.summary" rows="4" placeholder="Brief description of the product..."></textarea>
          </div>
        </div>

        <div class="dialog-actions">
          <button class="btn-cancel" @click="dlg = false">Cancel</button>
          <button class="btn-save" :disabled="saving" @click="save">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H12.5L17.5 7.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14.1667 17.5V10.8333H5.83333V17.5M5.83333 2.5V7.5H11.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase'
import {
  collection, addDoc, onSnapshot, orderBy, query, serverTimestamp,
  updateDoc, doc, deleteDoc
} from 'firebase/firestore'

export default {
  name: 'AdminProducts',
  data() {
    return {
      loading: true,
      products: [],
      unsub: null,
      search: '',
      dlg: false,
      saving: false,
      deletingId: '',
      form: { id: '', name: '', slug: '', summary: '', status: 'active' }
    }
  },
  created() {
    const qRef = query(collection(db, 'products'), orderBy('updatedAt', 'desc'))
    this.unsub = onSnapshot(qRef, snap => {
      this.products = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      this.loading = false
    })
  },
  beforeDestroy() {
    if (this.unsub) this.unsub()
  },
  computed: {
    filtered() {
      const q = (this.search || '').toLowerCase()
      return this.products.filter(p =>
        [p.name, p.summary, p.slug].join(' ').toLowerCase().includes(q)
      )
    }
  },
  methods: {
    initials(name = '') {
      return name.split(/\s+/).map(x => x[0]).join('').slice(0, 2).toUpperCase()
    },
    avatarColor(name = '') {
      const colors = ['purple', 'cyan', 'pink', 'yellow', 'green']
      const i = Math.abs(name.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % colors.length
      return colors[i]
    },
    toDate(ts) {
      if (!ts) return null
      if (ts.toDate) return ts.toDate()
      if (ts.seconds) return new Date(ts.seconds * 1000)
      if (typeof ts === 'number') return new Date(ts)
      return null
    },
    prettyTs(ts) {
      const d = this.toDate(ts)
      return d ? d.toLocaleString(undefined, { month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'
    },
    since(ts) {
      const d = this.toDate(ts)
      if (!d) return '—'
      const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000))
      const units = [
        ['y', 31536000], ['mo', 2592000], ['d', 86400],
        ['h', 3600], ['m', 60], ['s', 1]
      ]
      for (const [label, sec] of units) {
        const v = Math.floor(s / sec)
        if (v >= 1) return `${v}${label} ago`
      }
      return 'just now'
    },
    slugify(str = '') {
      return String(str).toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
        .slice(0, 64)
    },
    autoSlug() {
      if (!this.form.id && !this.form.slug) {
        this.form.slug = this.slugify(this.form.name)
      }
    },
    openCreate() {
      this.form = { id: '', name: '', slug: '', summary: '', status: 'active' }
      this.dlg = true
    },
    edit(item) {
      this.form = { ...item }
      this.dlg = true
    },
    async save() {
      this.saving = true
      try {
        const payload = {
          name: this.form.name,
          slug: this.form.slug || this.slugify(this.form.name),
          summary: this.form.summary,
          status: this.form.status || 'active',
          updatedAt: serverTimestamp(),
          visibility: 'team',
        }
        if (!this.form.id) {
          await addDoc(collection(db, 'products'), {
            ...payload,
            createdAt: serverTimestamp(),
          })
        } else {
          await updateDoc(doc(db, 'products', this.form.id), payload)
        }
        this.dlg = false
      } finally {
        this.saving = false
      }
    },
    async removeItem(item) {
      this.deletingId = item.id
      try {
        await deleteDoc(doc(db, 'products', item.id))
      } finally {
        this.deletingId = ''
      }
    },
    openWorkspace(item) {
      const id = item && item.id ? item.id : (item?.item?.id)
      if (!id) return
      this.$router.push({ name: 'AdminProductDetail', params: { id } }).catch(() => {
        this.$router.push(`/admin/products/${id}`)
      })
    }
  }
}
</script>

<style scoped>

.products-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at 20% 20%, rgba(123, 47, 247, 0.3), transparent 45%),
    radial-gradient(circle at 80% 80%, rgba(255, 0, 110, 0.3), transparent 45%),
    radial-gradient(circle at 50% 50%, rgba(0, 255, 245, 0.2), transparent 50%),
    linear-gradient(135deg, #1a0b2e 0%, #0a0118 50%, #1a0b2e 100%);
  background-size: 200% 200%;
  animation: bg-breathe 25s ease-in-out infinite;
  position: relative;
  font-family: 'Sora', sans-serif;
  padding: 32px 24px 80px;
}

@keyframes bg-breathe {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.page-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(to right, rgba(0, 255, 245, 0.08) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(123, 47, 247, 0.08) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
  animation: grid-flow 35s linear infinite;
  pointer-events: none;
  z-index: 0;
}

@keyframes grid-flow {
  0% { transform: translate(0, 0); }
  100% { transform: translate(60px, 60px); }
}

.page-orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(140px);
  opacity: 0.5;
  animation: orb-float 18s ease-in-out infinite;
  mix-blend-mode: screen;
  pointer-events: none;
  z-index: 0;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, #00fff5, transparent);
  top: -200px;
  right: -200px;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, #ff006e, transparent);
  bottom: -150px;
  left: -150px;
  animation-delay: 6s;
}

@keyframes orb-float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(50px, -50px) scale(1.1); }
  66% { transform: translate(-40px, 40px) scale(0.9); }
}

/* Products Container */
.products-container {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.products-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 32px;
  background: linear-gradient(90deg, #7b2ff7, #ff006e);
  border-radius: 20px 20px 0 0;
  margin-bottom: 0;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  color: #ffffff;
  backdrop-filter: blur(10px);
}

.header-text h1 {
  font-size: 24px;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 4px;
  line-height: 1;
}

.header-text p {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.25);
  border-radius: 10px;
  transition: all 0.3s ease;
  min-width: 280px;
  backdrop-filter: blur(10px);
}

.search-box:focus-within {
  background: rgba(255, 255, 255, 0.25);
  border-color: #00fff5;
  box-shadow: 0 0 20px rgba(0, 255, 245, 0.4);
}

.search-box svg {
  color: rgba(255, 255, 255, 0.8);
  flex-shrink: 0;
}

.search-box input {
  flex: 1;
  background: transparent;
  border: none;
  color: #ffffff;
  font-family: 'Sora', sans-serif;
  font-size: 14px;
  outline: none;
}

.search-box input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.btn-new-product {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #ffffff;
  border: none;
  border-radius: 10px;
  color: #7b2ff7;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Sora', sans-serif;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.btn-new-product:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
}

/* Table Card */
.products-table-card {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid #7b2ff7;
  border-top: none;
  border-radius: 0 0 20px 20px;
  backdrop-filter: blur(20px);
  box-shadow: 0 0 40px rgba(123, 47, 247, 0.3);
  overflow: hidden;
}

/* Loading */
.table-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  gap: 16px;
}

.loader {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top-color: #00fff5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.table-loading p {
  font-size: 15px;
  color: #b8b8d1;
}

/* Empty State */
.table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
  color: #b8b8d1;
}

.table-empty svg {
  opacity: 0.3;
  margin-bottom: 20px;
}

.table-empty p {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 8px;
}

.table-empty span {
  font-size: 14px;
}

.table-empty strong {
  color: #00fff5;
}

/* Table Content */
.table-content {
  padding: 0;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 120px 260px 130px;
  gap: 16px;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.th {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Space Mono', monospace;
}

.table-body {
  padding: 12px;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 120px 260px 130px;
  gap: 16px;
  padding: 16px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.table-row:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(0, 255, 245, 0.3);
  transform: translateX(4px);
  box-shadow: 0 4px 20px rgba(0, 255, 245, 0.2);
}

.td {
  display: flex;
  align-items: center;
}

.td-name {
  gap: 12px;
}

.product-avatar {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  font-family: 'Space Mono', monospace;
  flex-shrink: 0;
}

.avatar-purple { background: linear-gradient(135deg, #7b2ff7, #ff006e); }
.avatar-cyan { background: linear-gradient(135deg, #00fff5, #7b2ff7); }
.avatar-pink { background: linear-gradient(135deg, #ff006e, #fb5607); }
.avatar-yellow { background: linear-gradient(135deg, #ffbe0b, #fb5607); }
.avatar-green { background: linear-gradient(135deg, #4ade80, #22c55e); }

.product-info {
  flex: 1;
  min-width: 0;
}

.product-name {
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-summary {
  font-size: 13px;
  color: #b8b8d1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  font-family: 'Space Mono', monospace;
  text-transform: capitalize;
}

.status-active {
  background: rgba(74, 222, 128, 0.2);
  border: 1px solid rgba(74, 222, 128, 0.4);
  color: #4ade80;
}

.status-paused {
  background: rgba(251, 191, 36, 0.2);
  border: 1px solid rgba(251, 191, 36, 0.4);
  color: #fbbf24;
}

.status-archived {
  background: rgba(156, 163, 175, 0.2);
  border: 1px solid rgba(156, 163, 175, 0.4);
  color: #9ca3af;
}

.td-updated {
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.updated-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #b8b8d1;
  font-family: 'Space Mono', monospace;
}

.updated-info svg {
  color: #00fff5;
}

.updated-ago {
  padding: 2px 8px;
  background: rgba(0, 255, 245, 0.15);
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #00fff5;
  font-family: 'Space Mono', monospace;
}

.td-actions {
  gap: 6px;
  justify-content: flex-end;
}

.action-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover:not(:disabled) {
  transform: translateY(-2px);
}

.btn-open {
  color: #00fff5;
}

.btn-open:hover {
  background: rgba(0, 255, 245, 0.15);
  border-color: #00fff5;
  box-shadow: 0 0 15px rgba(0, 255, 245, 0.3);
}

.btn-edit {
  color: #7b2ff7;
}

.btn-edit:hover {
  background: rgba(123, 47, 247, 0.15);
  border-color: #7b2ff7;
  box-shadow: 0 0 15px rgba(123, 47, 247, 0.3);
}

.btn-delete {
  color: #f87171;
}

.btn-delete:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.15);
  border-color: #f87171;
  box-shadow: 0 0 15px rgba(239, 68, 68, 0.3);
}

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mini-loader {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(248, 113, 113, 0.3);
  border-top-color: #f87171;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Dialog */
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
  animation: fade-in 0.2s ease;
}

@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.dialog-card {
  width: 100%;
  max-width: 560px;
  background: linear-gradient(135deg, rgba(26, 11, 46, 0.98), rgba(10, 1, 24, 0.98));
  border: 2px solid #00fff5;
  border-radius: 20px;
  backdrop-filter: blur(20px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.7);
  animation: dialog-appear 0.3s ease;
}

@keyframes dialog-appear {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 32px;
  background: linear-gradient(90deg, #00fff5, #7b2ff7);
  border-radius: 18px 18px 0 0;
  position: relative;
}

.dialog-title {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.dialog-title svg {
  color: #ffffff;
  filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.5));
}

.dialog-title span {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
}

.slug-preview {
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
  font-family: 'Space Mono', monospace;
}

.dialog-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dialog-close:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

.dialog-body {
  padding: 32px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'Space Mono', monospace;
  margin-bottom: 8px;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #ffffff;
  font-family: 'Sora', sans-serif;
  font-size: 15px;
  outline: none;
  transition: all 0.3s ease;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  background: rgba(255, 255, 255, 0.12);
  border-color: #00fff5;
  box-shadow: 0 0 20px rgba(0, 255, 245, 0.3);
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.form-group select option {
  background: #1a0b2e;
  color: #ffffff;
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-cancel {
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Sora', sans-serif;
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.1);
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #00fff5, #7b2ff7);
  border: none;
  border-radius: 10px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 255, 245, 0.4);
  font-family: 'Sora', sans-serif;
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 30px rgba(0, 255, 245, 0.6);
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 1024px) {
  .table-header,
  .table-row {
    grid-template-columns: 2fr 100px 1fr 110px;
  }
}

@media (max-width: 768px) {
  .products-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-right {
    width: 100%;
    flex-direction: column;
  }

  .search-box {
    width: 100%;
    min-width: 0;
  }

  .btn-new-product {
    width: 100%;
    justify-content: center;
  }

  .table-header {
    display: none;
  }

  .table-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .td-actions {
    justify-content: flex-start;
  }
}
</style>